import React, { useState, useEffect, useCallback } from 'react';import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Bug, Search, RefreshCw, Trash2, Download, Filter, 
  AlertCircle, AlertTriangle, Info, CheckCircle, XCircle,
  Clock, Database, Mail, Cloud, Shield, Activity,
  ChevronLeft, ChevronRight, Terminal,
  ChevronDown, ChevronUp,
  Monitor, User, MapPin, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const DebuggerPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    level: 'all',
    search: '',
    source: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    totalPages: 0
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  const [activeDebugTab, setActiveDebugTab] = useState('logs');
  const [expandedRecentErrors, setExpandedRecentErrors] = useState(new Set());
  
  // Email logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailPagination, setEmailPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    totalPages: 0
  });
  const [emailFilter, setEmailFilter] = useState('all'); // all, success, failed

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.perPage
      });
      
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.level !== 'all') params.append('level', filters.level);
      if (filters.search) params.append('search', filters.search);
      if (filters.source !== 'all') params.append('source', filters.source);
      
      const response = await api.get(`/debugger/logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Error al cargar logs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.perPage]);

  const loadStats = async () => {
    try {
      const response = await api.get('/debugger/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadEmailLogs = async () => {
    try {
      setEmailLoading(true);
      const params = new URLSearchParams({
        page: emailPagination.page,
        per_page: emailPagination.perPage
      });
      if (emailFilter === 'success') params.append('success', 'true');
      if (emailFilter === 'failed') params.append('success', 'false');
      
      const response = await api.get(`/email/logs?${params.toString()}`);
      setEmailLogs(response.data.logs || []);
      setEmailPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      console.error('Error loading email logs:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  const loadEmailStats = async () => {
    try {
      const response = await api.get('/email/logs/stats');
      setEmailStats(response.data);
    } catch (error) {
      console.error('Error loading email stats:', error);
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [loadLogs]);

  useEffect(() => {
    if (activeDebugTab === 'emails') {
      loadEmailLogs();
      loadEmailStats();
    }
  }, [activeDebugTab, emailPagination.page, emailFilter]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadLogs();
        loadStats();
        if (activeDebugTab === 'emails') {
          loadEmailLogs();
          loadEmailStats();
        }
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, loadLogs, activeDebugTab]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearOldLogs = async (days) => {
    if (!window.confirm(`¿Eliminar logs con más de ${days} días de antigüedad?`)) return;
    
    try {
      const response = await api.delete(`/debugger/logs?days_old=${days}`);
      toast.success(`${response.data.deleted_count} logs eliminados`);
      loadLogs();
      loadStats();
    } catch (error) {
      toast.error('Error al eliminar logs');
    }
  };

  const handleExportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug_logs_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Logs exportados');
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'info': return <Info className="h-4 w-4 text-blue-500" />;
      case 'debug': return <CheckCircle className="h-4 w-4 text-gray-500" />;
      default: return <Info className="h-4 w-4 text-gray-400" />;
    }
  };

  const getLevelBadge = (level) => {
    const colors = {
      error: 'bg-red-500/10 text-red-500 border-red-500/20',
      warning: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      info: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      debug: 'bg-gray-500/10 text-gray-500 border-gray-500/20'
    };
    return colors[level] || colors.info;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'onedrive': return <Cloud className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'backup': return <Database className="h-4 w-4" />;
      case 'auth': return <Shield className="h-4 w-4" />;
      case 'audit': return <Activity className="h-4 w-4" />;
      default: return <Terminal className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      onedrive: 'text-blue-400',
      email: 'text-purple-400',
      backup: 'text-green-400',
      auth: 'text-amber-400',
      audit: 'text-cyan-400',
      system: 'text-gray-400',
      test: 'text-pink-400'
    };
    return colors[category] || 'text-gray-400';
  };

  // Función para explicar errores de forma simple
  const getErrorExplanation = (log) => {
    if (log.level !== 'error' && log.level !== 'warning') return null;
    
    const message = (log.message || '').toLowerCase();
    const category = (log.category || '').toLowerCase();
    const details = JSON.stringify(log.details || {}).toLowerCase();
    
    // Diccionario de explicaciones comunes - Ahora con explicaciones más extensas
    const explanations = {
      // Errores de validación
      validation: {
        match: () => message.includes('validation') || message.includes('validación') || details.includes('validationerror'),
        title: 'Error de Validación de Datos',
        explanation: 'El sistema recibió datos con un formato incorrecto, tipo de dato equivocado, o campos requeridos faltantes. Esto sucede cuando un formulario envía información que no cumple con las reglas definidas.',
        detailed: 'Este tipo de error ocurre cuando: 1) Un campo obligatorio está vacío, 2) El formato de un dato no es el esperado (ej: texto donde debería ir un número), 3) Un valor está fuera del rango permitido, 4) El tipo de dato enviado no coincide con el esperado por el servidor.',
        suggestion: 'Revisa el formulario que enviaste y asegúrate de: llenar todos los campos marcados como obligatorios (*), usar el formato correcto para fechas (DD/MM/AAAA) y números, no dejar espacios en blanco al inicio o final de los campos.'
      },
      // Errores de conexión
      connection: {
        match: () => message.includes('connection') || message.includes('conexión') || message.includes('timeout') || message.includes('network'),
        title: 'Error de Conexión de Red',
        explanation: 'El sistema no pudo establecer comunicación con un servicio externo. Esto puede ser la base de datos, servidor de email, servicio de respaldo en la nube, o cualquier API externa.',
        detailed: 'Posibles causas: 1) Interrupción temporal de internet, 2) El servidor destino está caído o en mantenimiento, 3) Firewall o proxy bloqueando la conexión, 4) El tiempo de espera (timeout) se agotó porque el servidor tardó demasiado en responder, 5) DNS no puede resolver el dominio del servicio.',
        suggestion: 'Pasos a seguir: 1) Verifica tu conexión a internet, 2) Espera unos minutos e intenta de nuevo, 3) Si usas VPN, intenta desconectarla temporalmente, 4) Si el problema persiste más de 10 minutos, contacta al soporte técnico.'
      },
      // Errores de autenticación
      auth: {
        match: () => message.includes('auth') || message.includes('token') || message.includes('unauthorized') || message.includes('forbidden') || category === 'auth',
        title: 'Error de Autenticación o Autorización',
        explanation: 'Hubo un problema verificando tu identidad o permisos. Tu sesión puede haber expirado, tu token de acceso puede ser inválido, o no tienes los permisos necesarios para esta acción.',
        detailed: 'Esto puede ocurrir por: 1) Sesión expirada después de mucho tiempo de inactividad, 2) Inicio de sesión desde otro dispositivo que invalidó esta sesión, 3) Tu cuenta fue modificada por un administrador, 4) El token JWT expiró o fue revocado, 5) Intentas acceder a una función para la cual tu rol no tiene permisos.',
        suggestion: 'Solución: 1) Cierra sesión completamente (logout), 2) Limpia las cookies del navegador para este sitio, 3) Vuelve a iniciar sesión, 4) Si no puedes acceder después de esto, contacta al administrador para verificar el estado de tu cuenta y permisos.'
      },
      // Errores de base de datos
      database: {
        match: () => message.includes('database') || message.includes('mongo') || message.includes('db') || category === 'backup',
        title: 'Error de Base de Datos',
        explanation: 'Ocurrió un problema al intentar guardar, actualizar, eliminar o recuperar información de la base de datos MongoDB. Esto puede afectar la persistencia de los datos.',
        detailed: 'Causas comunes: 1) La conexión a MongoDB se perdió momentáneamente, 2) El documento que intentas guardar tiene un formato inválido, 3) Hay un conflicto de IDs duplicados, 4) La base de datos está temporalmente sobrecargada, 5) Un índice único fue violado (ej: intentar crear un usuario con un nombre que ya existe), 6) El disco del servidor está lleno.',
        suggestion: 'Acciones recomendadas: 1) Intenta la operación nuevamente en unos segundos, 2) Si estás creando un registro, verifica que no exista uno igual, 3) Si el error persiste, revisa los logs del servidor para más detalles, 4) En casos graves, puede ser necesario verificar el estado del servidor de base de datos.'
      },
      // Errores de email
      email: {
        match: () => message.includes('email') || message.includes('smtp') || message.includes('correo') || category === 'email',
        title: 'Error de Envío de Correo Electrónico',
        explanation: 'El sistema no pudo enviar un correo electrónico. Esto puede deberse a problemas con el servidor SMTP configurado, credenciales inválidas, o direcciones de correo incorrectas.',
        detailed: 'Posibles problemas: 1) Las credenciales SMTP (usuario/contraseña) son incorrectas, 2) El servidor SMTP requiere una "App Password" en lugar de la contraseña normal (común en Microsoft 365 y Gmail), 3) El puerto SMTP está bloqueado, 4) El servidor de destino rechazó el correo (filtro de spam), 5) La dirección de correo del destinatario no existe o está mal escrita, 6) Se alcanzó el límite diario de envíos.',
        suggestion: 'Para resolver: 1) Verifica la configuración SMTP en Ajustes > Email, 2) Asegúrate de usar una "App Password" si usas Microsoft 365 o Gmail, 3) Verifica que "Security Defaults" esté desactivado en Azure AD (para Office 365), 4) Confirma que la dirección de destino sea correcta, 5) Usa la función "Enviar email de prueba" para diagnosticar.'
      },
      // Errores de OneDrive
      onedrive: {
        match: () => message.includes('onedrive') || message.includes('microsoft') || category === 'onedrive',
        title: 'Error de Integración con OneDrive/Microsoft',
        explanation: 'Hubo un problema al comunicarse con los servicios de Microsoft (OneDrive) para respaldos automáticos o almacenamiento de archivos.',
        detailed: 'Causas frecuentes: 1) El token de acceso a OneDrive expiró y necesita renovarse, 2) Los permisos de la aplicación fueron revocados en la cuenta de Microsoft, 3) La cuota de almacenamiento en OneDrive está llena, 4) El archivo que intentas respaldar es demasiado grande, 5) Problemas temporales con los servidores de Microsoft, 6) La cuenta de OneDrive fue suspendida o eliminada.',
        suggestion: 'Pasos para solucionar: 1) Ve a Ajustes > Respaldos y verifica el estado de la conexión con OneDrive, 2) Si es necesario, re-autoriza la aplicación haciendo clic en "Conectar OneDrive", 3) Verifica que tengas espacio disponible en tu cuenta de OneDrive, 4) Intenta hacer un respaldo manual para ver si el problema persiste.'
      },
      // Errores de archivo
      file: {
        match: () => message.includes('file') || message.includes('archivo') || message.includes('upload') || message.includes('pdf'),
        title: 'Error de Procesamiento de Archivo',
        explanation: 'Ocurrió un problema al intentar subir, procesar, generar o descargar un archivo. Puede ser una imagen, PDF, documento de Excel o cualquier otro tipo de archivo.',
        detailed: 'Posibles causas: 1) El archivo está corrupto o dañado, 2) El formato del archivo no es el esperado (ej: .docx cuando se esperaba .pdf), 3) El archivo excede el tamaño máximo permitido, 4) El nombre del archivo contiene caracteres especiales no válidos, 5) No hay espacio suficiente en el servidor para guardar el archivo, 6) Error al generar el PDF (puede faltar una fuente o imagen).',
        suggestion: 'Soluciones: 1) Verifica que el archivo no esté dañado intentando abrirlo localmente, 2) Asegúrate de usar el formato correcto (.pdf, .xlsx, .jpg, etc.), 3) Si el archivo es muy grande, intenta comprimirlo o dividirlo, 4) Elimina caracteres especiales (ñ, acentos, símbolos) del nombre del archivo, 5) Para PDFs, intenta regenerar el documento.'
      },
      // Error no encontrado
      notfound: {
        match: () => message.includes('not found') || message.includes('no encontrad') || message.includes('404'),
        title: 'Recurso No Encontrado (404)',
        explanation: 'El sistema intentó acceder a un recurso (registro, archivo, página, endpoint de API) que no existe o ya no está disponible.',
        detailed: 'Esto puede pasar cuando: 1) El registro fue eliminado por otro usuario, 2) El ID del recurso está mal escrito o es incorrecto, 3) La URL de la página cambió o ya no existe, 4) Intentas acceder a un archivo que fue movido o eliminado, 5) Un enlace o referencia está desactualizado, 6) El endpoint de API ya no existe en esta versión del sistema.',
        suggestion: 'Qué hacer: 1) Verifica que el recurso exista y no haya sido eliminado, 2) Si usaste un enlace guardado, intenta navegar desde el menú principal, 3) Actualiza la página (F5) para refrescar los datos, 4) Si buscas un elemento eliminado, revisa la sección de "Eliminados" donde puede ser restaurado.'
      },
      // Error de permisos
      permission: {
        match: () => message.includes('permission') || message.includes('permiso') || message.includes('access denied') || message.includes('403'),
        title: 'Error de Permisos de Acceso',
        explanation: 'Tu cuenta de usuario no tiene los permisos necesarios para realizar esta acción. Cada rol en el sistema tiene permisos específicos.',
        detailed: 'Tu rol actual puede no tener acceso a: 1) Ciertas páginas o secciones del sistema, 2) Crear, editar o eliminar ciertos tipos de registros, 3) Ver información confidencial o sensible, 4) Ejecutar acciones administrativas, 5) Descargar o exportar ciertos reportes. Los permisos son configurados por el administrador del sistema.',
        suggestion: 'Opciones disponibles: 1) Contacta al administrador del sistema para solicitar los permisos necesarios, 2) Verifica que estés intentando acceder con la cuenta correcta, 3) Si crees que deberías tener acceso, pide al admin que revise tu rol en Usuarios > [Tu usuario] > Permisos.'
      },
      // Error interno del servidor
      server: {
        match: () => message.includes('500') || message.includes('internal') || message.includes('server error'),
        title: 'Error Interno del Servidor (500)',
        explanation: 'Ocurrió un error inesperado en el servidor. Esto generalmente indica un bug en el código o una situación que el sistema no supo manejar correctamente.',
        detailed: 'Este tipo de error puede ser causado por: 1) Un bug o defecto en el código del servidor, 2) Datos corruptos o inconsistentes en la base de datos, 3) Falta de memoria o recursos en el servidor, 4) Una dependencia externa que falló, 5) Un escenario no contemplado en la programación. Estos errores requieren atención del equipo técnico.',
        suggestion: 'Qué puedes hacer: 1) Espera unos segundos e intenta de nuevo, 2) Si el error se repite, anota qué acción estabas realizando y qué datos ingresaste, 3) Reporta el error al soporte técnico incluyendo: la hora exacta, qué intentabas hacer, y cualquier mensaje de error visible, 4) Copia el JSON de "Detalles técnicos" para compartirlo con soporte.'
      },
      // Error de API
      api_error: {
        match: () => category === 'api_error' || message.includes('api') || message.includes('endpoint'),
        title: 'Error en Llamada a API',
        explanation: 'Una solicitud al servidor (API) falló. Esto puede afectar la funcionalidad que estabas intentando usar.',
        detailed: 'Información técnica: Las APIs son los "canales de comunicación" entre la interfaz de usuario y el servidor. Cuando falla una API, puede ser por: 1) Datos enviados con formato incorrecto, 2) El servidor rechazó la petición por validación, 3) Error de autenticación, 4) El endpoint no existe, 5) Error interno procesando la solicitud. El mensaje técnico y los detalles JSON contienen información específica del error.',
        suggestion: 'Para el equipo técnico: Revisa el método HTTP, la ruta del endpoint, el código de estado, y el traceback en los detalles. Para usuarios: Intenta la acción nuevamente, si persiste reporta el error incluyendo los detalles técnicos mostrados aquí.'
      }
    };
    
    // Buscar la primera explicación que coincida
    for (const [key, exp] of Object.entries(explanations)) {
      if (exp.match()) {
        return exp;
      }
    }
    
    // Explicación genérica si no hay coincidencia
    return {
      title: log.level === 'error' ? 'Error del Sistema' : 'Advertencia del Sistema',
      explanation: log.level === 'error' 
        ? 'Ocurrió un error en el sistema que no pudo ser categorizado automáticamente. Revisa el mensaje técnico y los detalles JSON para más información.'
        : 'El sistema detectó una situación que podría requerir atención pero no es crítica.',
      detailed: 'Este mensaje no coincide con ningún patrón de error conocido. El mensaje técnico completo y el JSON de detalles contienen la información necesaria para diagnosticar el problema. Comparte esta información con el equipo técnico si necesitas ayuda.',
      suggestion: 'Revisa los detalles técnicos mostrados abajo. Si no entiendes el error, copia toda la información (especialmente el JSON de detalles) y compártela con el equipo de soporte técnico para que puedan diagnosticar el problema.'
    };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="debugger-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="h-6 w-6 text-primary" />
            Debugger
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor de logs y eventos del sistema
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto (10s)' : 'Auto-refresh'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadLogs(); loadStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickeable filters */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 cursor-pointer transition-all hover:scale-[1.02] ${filters.level === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleFilterChange('level', 'all')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Logs</p>
                  <p className="text-2xl font-bold text-blue-500">{stats.total_logs?.toLocaleString() || 0}</p>
                </div>
                <Database className="h-8 w-8 text-blue-500/50" />
              </div>
              {filters.level === 'all' && <p className="text-xs text-blue-400 mt-1">✓ Mostrando todos</p>}
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20 cursor-pointer transition-all hover:scale-[1.02] ${filters.level === 'error' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleFilterChange('level', filters.level === 'error' ? 'all' : 'error')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Errores</p>
                  <p className="text-2xl font-bold text-red-500">{stats.levels?.error || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500/50" />
              </div>
              {filters.level === 'error' && <p className="text-xs text-red-400 mt-1">✓ Filtro activo</p>}
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 cursor-pointer transition-all hover:scale-[1.02] ${filters.level === 'warning' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => handleFilterChange('level', filters.level === 'warning' ? 'all' : 'warning')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Advertencias</p>
                  <p className="text-2xl font-bold text-amber-500">{stats.levels?.warning || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-500/50" />
              </div>
              {filters.level === 'warning' && <p className="text-xs text-amber-400 mt-1">✓ Filtro activo</p>}
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20 cursor-pointer transition-all hover:scale-[1.02] ${filters.category === 'audit' ? 'ring-2 ring-cyan-500' : ''}`}
            onClick={() => handleFilterChange('category', filters.category === 'audit' ? 'all' : 'audit')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Auditoría</p>
                  <p className="text-2xl font-bold text-cyan-500">{stats.categories?.audit || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-cyan-500/50" />
              </div>
              {filters.category === 'audit' && <p className="text-xs text-cyan-400 mt-1">✓ Filtro activo</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs - Manual implementation */}
      <div className="w-full">
        <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1 w-full max-w-2xl">
          <button
            type="button"
            onClick={() => setActiveDebugTab('logs')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeDebugTab === 'logs' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-logs"
          >
            <Terminal className="h-4 w-4 mr-2" />
            Logs
          </button>
          <button
            type="button"
            onClick={() => setActiveDebugTab('errors')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeDebugTab === 'errors' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-errors"
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            Errores Recientes
          </button>
          <button
            type="button"
            onClick={() => setActiveDebugTab('emails')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeDebugTab === 'emails' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-emails"
          >
            <Mail className="h-4 w-4 mr-2" />
            Correos
          </button>
          <button
            type="button"
            onClick={() => setActiveDebugTab('status-audit')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeDebugTab === 'status-audit'
                ? 'bg-background text-foreground shadow-sm'
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-status-audit"
          >
            <Database className="h-4 w-4 mr-2" />
            Status Audit
          </button>
          <button
            type="button"
            onClick={() => setActiveDebugTab('inventory-health')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeDebugTab === 'inventory-health'
                ? 'bg-background text-foreground shadow-sm'
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-inventory-health"
          >
            <Activity className="h-4 w-4 mr-2" />
            Salud del Inventario
          </button>
        </div>

        {/* Inventory Health Tab Content (v5.4.11) */}
        {activeDebugTab === 'inventory-health' && (
          <InventoryHealthPanel />
        )}

        {/* Status Audit Tab Content (v5.4.4) */}
        {activeDebugTab === 'status-audit' && (
          <StatusAuditPanel />
        )}

        {/* Emails Tab Content */}
        {activeDebugTab === 'emails' && (
          <div className="space-y-4 mt-2">
            {/* Email Stats */}
            {emailStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{emailStats.total}</p>
                        <p className="text-xs text-muted-foreground">Total Enviados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{emailStats.successful}</p>
                        <p className="text-xs text-muted-foreground">Exitosos</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">{emailStats.failed}</p>
                        <p className="text-xs text-muted-foreground">Fallidos</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{emailStats.success_rate}%</p>
                        <p className="text-xs text-muted-foreground">Tasa de Éxito</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Filters and Table */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-2">
                    <Button
                      variant={emailFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setEmailFilter('all'); setEmailPagination(p => ({...p, page: 1})); }}
                    >
                      Todos
                    </Button>
                    <Button
                      variant={emailFilter === 'success' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setEmailFilter('success'); setEmailPagination(p => ({...p, page: 1})); }}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Exitosos
                    </Button>
                    <Button
                      variant={emailFilter === 'failed' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setEmailFilter('failed'); setEmailPagination(p => ({...p, page: 1})); }}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Fallidos
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { loadEmailLogs(); loadEmailStats(); }}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
                  </Button>
                </div>
                
                {emailLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : emailLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay registros de correos</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-sm font-medium">Fecha</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Destinatario</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Asunto</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Proveedor</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Estado</th>
                          <th className="text-left py-2 px-3 text-sm font-medium">Enviado por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr key={log.id} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-3 text-sm">
                              {new Date(log.timestamp).toLocaleString('es-MX', { 
                                day: '2-digit', month: '2-digit', year: '2-digit',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </td>
                            <td className="py-2 px-3 text-sm font-mono">{log.recipient}</td>
                            <td className="py-2 px-3 text-sm max-w-xs truncate" title={log.subject}>
                              {log.subject}
                            </td>
                            <td className="py-2 px-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                log.provider === 'brevo' ? 'bg-blue-100 text-blue-800' :
                                log.provider === 'msgraph' ? 'bg-purple-100 text-purple-800' :
                                log.provider === 'sendgrid' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {log.provider === 'brevo' ? 'Brevo' :
                                 log.provider === 'msgraph' ? 'MS365' :
                                 log.provider === 'sendgrid' ? 'SendGrid' :
                                 log.provider || 'N/A'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-sm">
                              {log.success ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="h-4 w-4" /> Enviado
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-red-600" title={log.error_message}>
                                  <XCircle className="h-4 w-4" /> Fallido
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-sm text-muted-foreground">
                              {log.sent_by || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination */}
                {emailPagination.totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-sm text-muted-foreground">
                      Mostrando {((emailPagination.page - 1) * emailPagination.perPage) + 1} - {Math.min(emailPagination.page * emailPagination.perPage, emailPagination.total)} de {emailPagination.total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={emailPagination.page === 1}
                        onClick={() => setEmailPagination(p => ({...p, page: p.page - 1}))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={emailPagination.page >= emailPagination.totalPages}
                        onClick={() => setEmailPagination(p => ({...p, page: p.page + 1}))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Logs Tab Content */}
        {activeDebugTab === 'logs' && (
          <div className="space-y-4 mt-2">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtros:</span>
                </div>
                
                <Select value={filters.category} onValueChange={(v) => handleFilterChange('category', v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="api_error">API Error</SelectItem>
                    <SelectItem value="audit">Auditoría</SelectItem>
                    <SelectItem value="onedrive">OneDrive</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="backup">Backup</SelectItem>
                    <SelectItem value="auth">Auth</SelectItem>
                    <SelectItem value="system">Sistema</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filters.level} onValueChange={(v) => handleFilterChange('level', v)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Nivel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="debug">Debug</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar en logs..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" onClick={handleExportLogs}>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </Button>
                  <Select onValueChange={(v) => handleClearOldLogs(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <Trash2 className="h-4 w-4 mr-2" />
                      <span>Limpiar</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Más de 7 días</SelectItem>
                      <SelectItem value="14">Más de 14 días</SelectItem>
                      <SelectItem value="30">Más de 30 días</SelectItem>
                      <SelectItem value="60">Más de 60 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Tiempo</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Nivel</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Categoría</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Mensaje</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Fuente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Cargando logs...
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No hay logs que mostrar
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, idx) => (
                        <React.Fragment key={log.id || `log-${idx}`}>
                          <tr 
                            className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${
                              log.level === 'error' ? 'bg-red-500/5' : 
                              log.level === 'warning' ? 'bg-amber-500/5' : ''
                            } ${selectedLog === idx ? 'bg-primary/10' : ''}`}
                            onClick={() => setSelectedLog(selectedLog === idx ? null : idx)}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {formatTimestamp(log.timestamp)}
                              </div>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline" className={getLevelBadge(log.level)}>
                                {getLevelIcon(log.level)}
                                <span className="ml-1 capitalize">{log.level}</span>
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className={`flex items-center gap-2 text-sm ${getCategoryColor(log.category)}`}>
                                {getCategoryIcon(log.category)}
                                <span className="capitalize">{log.category}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <p className="text-sm truncate max-w-md" title={log.message}>
                                {log.message}
                              </p>
                            </td>
                            <td className="p-3">
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                {log.source}
                              </span>
                            </td>
                          </tr>
                          {/* Expanded details row */}
                          {selectedLog === idx && (
                            <tr className="bg-muted/30">
                              <td colSpan={5} className="p-4 space-y-4">
                                {/* Special Authentication Error Card */}
                                {(() => {
                                  const isAuthError = log.action === 'failed_login' ||
                                                      log.type === 'failed_login' ||
                                                      log.category === 'auth_error' ||
                                                      log.message?.toLowerCase().includes('login fallido') ||
                                                      log.message?.toLowerCase().includes('failed_login') ||
                                                      log.details?.reason === 'wrong_password' ||
                                                      log.details?.reason === 'user_not_found';
                                  
                                  const failedAttempts = log.details?.failed_attempts || 0;
                                  const isHighRisk = failedAttempts >= 3;
                                  const borderColor = isHighRisk ? 'border-red-500/60' : 'border-amber-500/40';
                                  const headerBg = isHighRisk ? 'bg-red-500/30' : 'bg-amber-500/20';
                                  const headerBorder = isHighRisk ? 'border-red-500/40' : 'border-amber-500/30';
                                  const titleColor = isHighRisk ? 'text-red-400' : 'text-amber-400';
                                  const iconBg = isHighRisk ? 'bg-red-500/40' : 'bg-amber-500/30';
                                  
                                  if (isAuthError) {
                                    return (
                                      <div className={`rounded-lg border-2 ${borderColor} bg-gradient-to-br from-red-500/10 to-orange-500/5 overflow-hidden`}>
                                        {/* Auth Error Header */}
                                        <div className={`px-4 py-3 ${headerBg} border-b ${headerBorder} flex items-center gap-3`}>
                                          <div className={`p-2 ${iconBg} rounded-full`}>
                                            <Shield className={`h-5 w-5 ${titleColor}`} />
                                          </div>
                                          <div>
                                            <h3 className={`font-semibold ${titleColor}`}>
                                              {isHighRisk ? '⚠️ Alerta de Seguridad - Múltiples Intentos Fallidos' : 'Error de Autenticación'}
                                            </h3>
                                            <p className="text-xs text-muted-foreground">{formatTimestamp(log.timestamp)}</p>
                                          </div>
                                        </div>
                                        
                                        {/* Auth Error Details Grid */}
                                        <div className="p-4 space-y-4">
                                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                            {/* Username */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <User className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">Usuario</span>
                                              </div>
                                              <p className="font-mono font-medium text-sm truncate">
                                                {log.details?.username || log.details?.username_attempted || 'Desconocido'}
                                              </p>
                                            </div>
                                            
                                            {/* IP Address */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <Globe className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">Dirección IP</span>
                                              </div>
                                              <p className="font-mono font-medium text-sm">
                                                {log.details?.ip_address || log.details?.ip || 'No disponible'}
                                              </p>
                                            </div>
                                            
                                            {/* Country */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">País</span>
                                              </div>
                                              <p className="font-medium text-sm">
                                                {log.details?.country || 'Desconocido'}
                                              </p>
                                            </div>
                                            
                                            {/* City */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <MapPin className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">Ciudad</span>
                                              </div>
                                              <p className="font-medium text-sm">
                                                {log.details?.city || 'Desconocido'}
                                              </p>
                                            </div>
                                            
                                            {/* Browser */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <Monitor className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-xs text-muted-foreground">Navegador</span>
                                              </div>
                                              <p className="font-medium text-sm truncate">
                                                {log.details?.browser || 'Desconocido'}
                                              </p>
                                            </div>
                                            
                                            {/* Reason */}
                                            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                              <div className="flex items-center gap-2 mb-1">
                                                <AlertTriangle className="h-3 w-3 text-red-400" />
                                                <span className="text-xs text-muted-foreground">Razón</span>
                                              </div>
                                              <p className="font-medium text-sm text-red-400">
                                                {log.details?.reason === 'wrong_password' ? 'Contraseña incorrecta' :
                                                 log.details?.reason === 'user_not_found' ? 'Usuario no existe' :
                                                 log.details?.reason === 'account_locked' ? 'Cuenta bloqueada' :
                                                 log.details?.reason || 'Error de autenticación'}
                                              </p>
                                            </div>
                                          </div>
                                          
                                          {/* Failed attempts indicator */}
                                          {failedAttempts > 1 && (
                                            <div className={`flex items-center gap-2 p-2 ${failedAttempts >= 3 ? 'bg-red-500/20 border-red-500/40' : 'bg-amber-500/10 border-amber-500/30'} border rounded-lg`}>
                                              <AlertTriangle className={`h-4 w-4 ${failedAttempts >= 3 ? 'text-red-400' : 'text-amber-400'}`} />
                                              <span className={`text-sm ${failedAttempts >= 3 ? 'text-red-400 font-semibold' : 'text-amber-400'}`}>
                                                {failedAttempts} intentos fallidos consecutivos
                                                {failedAttempts >= 3 && ' - Posible intento de intrusión'}
                                              </span>
                                            </div>
                                          )}
                                          
                                          {/* User Agent if available */}
                                          {log.details?.user_agent && (
                                            <details className="group">
                                              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                                                <Terminal className="h-3 w-3" />
                                                Ver User Agent completo
                                              </summary>
                                              <p className="mt-2 text-xs font-mono text-muted-foreground bg-background p-2 rounded break-all">
                                                {log.details.user_agent}
                                              </p>
                                            </details>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                
                                {/* Error Explanation Section for non-auth errors */}
                                {(log.level === 'error' || log.level === 'warning') && 
                                 !['failed_login'].includes(log.action) && 
                                 !['failed_login'].includes(log.type) && 
                                 log.category !== 'auth_error' &&
                                 (() => {
                                  const explanation = getErrorExplanation(log);
                                  return explanation ? (
                                    <div className={`p-4 rounded-lg border ${
                                      log.level === 'error' 
                                        ? 'bg-red-500/5 border-red-500/20' 
                                        : 'bg-amber-500/5 border-amber-500/20'
                                    }`}>
                                      <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-full ${
                                          log.level === 'error' ? 'bg-red-500/20' : 'bg-amber-500/20'
                                        }`}>
                                          {log.level === 'error' 
                                            ? <AlertCircle className="h-5 w-5 text-red-500" />
                                            : <AlertTriangle className="h-5 w-5 text-amber-500" />
                                          }
                                        </div>
                                        <div className="flex-1">
                                          <h4 className={`font-semibold ${
                                            log.level === 'error' ? 'text-red-500' : 'text-amber-500'
                                          }`}>
                                            {explanation.title}
                                          </h4>
                                          <p className="text-sm text-muted-foreground mt-1">
                                            {explanation.explanation}
                                          </p>
                                          <div className="mt-3 p-2 bg-background/50 rounded border border-dashed">
                                            <p className="text-xs">
                                              <span className="font-medium text-primary">💡 Sugerencia: </span>
                                              {explanation.suggestion}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                                
                                {/* Technical Details */}
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <Terminal className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium">Detalles Técnicos</span>
                                  </div>
                                  <pre className="text-xs bg-background p-3 rounded-lg overflow-x-auto border max-h-64 overflow-y-auto">
                                    {JSON.stringify(log, null, 2)}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Mostrando {((pagination.page - 1) * pagination.perPage) + 1} - {Math.min(pagination.page * pagination.perPage, pagination.total)} de {pagination.total}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      {pagination.page} / {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {/* Errors Tab Content */}
        {activeDebugTab === 'errors' && (
          <div className="mt-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Errores Recientes
              </CardTitle>
              <CardDescription>
                Vista simplificada de los errores más recientes con explicaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="max-w-full overflow-hidden">
              {stats?.recent_errors?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_errors.map((error, idx) => {
                    const explanation = getErrorExplanation({...error, level: 'error'});
                    
                    // Check if this is an authentication error
                    const isAuthError = error.type === 'failed_login' ||
                                        error.category === 'auth_error' ||
                                        error.message?.toLowerCase().includes('login fallido') || 
                                        error.message?.toLowerCase().includes('failed_login') ||
                                        error.details?.reason === 'wrong_password' ||
                                        error.details?.reason === 'user_not_found';
                    
                    const isExpanded = expandedRecentErrors.has(idx);
                    const toggleExpanded = () => {
                      setExpandedRecentErrors(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(idx)) {
                          newSet.delete(idx);
                        } else {
                          newSet.add(idx);
                        }
                        return newSet;
                      });
                    };
                    
                    // Special card for authentication errors
                    if (isAuthError) {
                      const failedAttempts = error.details?.failed_attempts || 1;
                      const isHighRisk = failedAttempts >= 3;
                      const borderColor = isHighRisk ? 'border-red-500/60' : 'border-amber-500/40';
                      const headerBg = isHighRisk ? 'bg-red-500/30' : 'bg-amber-500/20';
                      const headerBorder = isHighRisk ? 'border-red-500/40' : 'border-amber-500/30';
                      const titleColor = isHighRisk ? 'text-red-400' : 'text-amber-400';
                      const iconBg = isHighRisk ? 'bg-red-500/40' : 'bg-amber-500/30';
                      
                      return (
                        <div key={idx} className={`rounded-lg border-2 ${borderColor} bg-gradient-to-br from-red-500/10 to-orange-500/5 overflow-hidden`}>
                          {/* Auth Error Header - Clickable */}
                          <div 
                            className={`px-4 py-3 ${headerBg} border-b ${headerBorder} flex items-center justify-between cursor-pointer hover:opacity-90 transition-opacity`}
                            onClick={toggleExpanded}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 ${iconBg} rounded-full`}>
                                <Shield className={`h-5 w-5 ${titleColor}`} />
                              </div>
                              <div>
                                <h3 className={`font-semibold ${titleColor}`}>
                                  {isHighRisk ? '⚠️ Alerta de Seguridad - Múltiples Intentos' : 'Error de Autenticación'}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{formatTimestamp(error.timestamp)}</span>
                                  <span>•</span>
                                  <span className="font-mono">{error.details?.username || error.details?.username_attempted || 'Desconocido'}</span>
                                  {error.details?.country && error.details?.city && (
                                    <>
                                      <span>•</span>
                                      <span className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {error.details.city}, {error.details.country}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {failedAttempts > 1 && (
                                <Badge variant="destructive" className={isHighRisk ? 'bg-red-500' : 'bg-amber-500'}>
                                  {failedAttempts} intentos
                                </Badge>
                              )}
                              {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                          
                          {/* Auth Error Details Grid - Collapsible */}
                          {isExpanded && (
                            <div className="p-4 space-y-4">
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                {/* Username */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <User className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Usuario</span>
                                  </div>
                                  <p className="font-mono font-medium text-sm truncate">
                                    {error.details?.username || error.details?.username_attempted || 'Desconocido'}
                                  </p>
                                </div>
                                
                                {/* IP Address */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Globe className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Dirección IP</span>
                                  </div>
                                  <p className="font-mono font-medium text-sm">
                                    {error.details?.ip_address || error.details?.ip || 'No disponible'}
                                  </p>
                                </div>
                                
                                {/* Country */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <MapPin className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">País</span>
                                  </div>
                                  <p className="font-medium text-sm">
                                    {error.details?.country || 'Desconocido'}
                                  </p>
                                </div>
                                
                                {/* City */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <MapPin className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Ciudad</span>
                                  </div>
                                  <p className="font-medium text-sm">
                                    {error.details?.city || 'Desconocido'}
                                  </p>
                                </div>
                                
                                {/* Browser */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Monitor className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Navegador</span>
                                  </div>
                                  <p className="font-medium text-sm truncate">
                                    {error.details?.browser || 'Desconocido'}
                                  </p>
                                </div>
                                
                                {/* Reason */}
                                <div className="p-3 bg-background/60 rounded-lg border border-border/50">
                                  <div className="flex items-center gap-2 mb-1">
                                    <AlertTriangle className="h-3 w-3 text-red-400" />
                                    <span className="text-xs text-muted-foreground">Razón</span>
                                  </div>
                                  <p className="font-medium text-sm text-red-400">
                                    {error.details?.reason === 'wrong_password' ? 'Contraseña incorrecta' :
                                     error.details?.reason === 'user_not_found' ? 'Usuario no existe' :
                                     error.details?.reason === 'account_locked' ? 'Cuenta bloqueada' :
                                     error.details?.reason || 'Error de autenticación'}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Failed attempts indicator */}
                              {failedAttempts > 1 && (
                                <div className={`flex items-center gap-2 p-2 ${isHighRisk ? 'bg-red-500/20 border-red-500/40' : 'bg-amber-500/10 border-amber-500/30'} border rounded-lg`}>
                                  <AlertTriangle className={`h-4 w-4 ${isHighRisk ? 'text-red-400' : 'text-amber-400'}`} />
                                  <span className={`text-sm ${isHighRisk ? 'text-red-400 font-semibold' : 'text-amber-400'}`}>
                                    {failedAttempts} intentos fallidos consecutivos
                                    {isHighRisk && ' - Posible intento de intrusión'}
                                  </span>
                                </div>
                              )}
                              
                              {/* User Agent if available */}
                              {error.details?.user_agent && (
                                <details className="group">
                                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                                    <Terminal className="h-3 w-3" />
                                    Ver User Agent completo
                                  </summary>
                                  <p className="mt-2 text-xs font-mono text-muted-foreground bg-background p-2 rounded break-all">
                                    {error.details.user_agent}
                                  </p>
                                </details>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Regular error display (all other error types) - also collapsible
                    return (
                      <div key={idx} className="rounded-lg bg-red-500/5 border border-red-500/20 overflow-hidden">
                        {/* Clickable Header */}
                        <div 
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-red-500/10 transition-colors"
                          onClick={toggleExpanded}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-red-500/20 rounded-full shrink-0">
                              <AlertCircle className="h-4 w-4 text-red-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20 shrink-0">
                                  {error.category}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {formatTimestamp(error.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 truncate">
                                {error.message?.substring(0, 80)}{error.message?.length > 80 ? '...' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="shrink-0 ml-2">
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Content */}
                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-3 border-t border-red-500/20 pt-3">
                            {/* Simple Explanation */}
                            {explanation && (
                              <div className="p-3 bg-background/50 rounded-lg border">
                                <div className="flex items-start gap-2">
                                  <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                  <div className="space-y-2">
                                    <p className="text-sm font-medium text-primary">{explanation.title}</p>
                                    <p className="text-sm text-muted-foreground">{explanation.explanation}</p>
                                    {explanation.detailed && (
                                      <div className="mt-2 p-2 bg-muted/30 rounded text-xs text-muted-foreground">
                                        <p className="font-medium mb-1">Explicación detallada:</p>
                                        <p>{explanation.detailed}</p>
                                      </div>
                                    )}
                                    <div className="mt-2 p-2 bg-primary/10 rounded border border-primary/20">
                                      <p className="text-sm">
                                        <span className="font-medium text-primary">💡 Sugerencia: </span>
                                        <span className="text-foreground">{explanation.suggestion}</span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Technical message */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-1 font-medium">Mensaje técnico completo:</p>
                              <p className="text-sm font-mono bg-background p-3 rounded break-all border">
                                {error.message}
                              </p>
                            </div>
                            
                            {/* Details */}
                            {error.details && Object.keys(error.details).length > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1 font-medium">Detalles técnicos (JSON):</p>
                                <div className="max-h-60 overflow-auto rounded border bg-background">
                                  <pre className="text-xs p-3 break-all" style={{wordBreak: 'break-all', whiteSpace: 'pre-wrap'}}>
                                    {JSON.stringify(error.details, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                  <p>No hay errores recientes</p>
                  <p className="text-sm">¡El sistema está funcionando correctamente!</p>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebuggerPage;

// ============================================================
// v5.4.4 — Status Audit Panel (superadmin DB hygiene utility)
// ============================================================
const StatusAuditPanel = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [normalizing, setNormalizing] = React.useState(null); // raw status currently being normalized
  const [overrideTarget, setOverrideTarget] = React.useState({}); // { rawStatus: chosenCanonical }

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/status-audit');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el audit');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const normalize = async (raw, target) => {
    if (!target) {
      toast.error('Selecciona un valor canónico destino');
      return;
    }
    if (!window.confirm(`¿Convertir ${data.rows.find(r => r.raw_status === raw)?.count ?? '?'} unidad(es) con status "${raw ?? '(vacío)'}" → "${target}"? Esta acción es permanente.`)) {
      return;
    }
    setNormalizing(raw);
    try {
      const res = await api.post('/admin/status-normalize', { from_status: raw, to_status: target });
      toast.success(`${res.data.modified} unidad(es) actualizada(s) → ${target}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo normalizar');
    } finally {
      setNormalizing(null);
    }
  };

  if (loading && !data) {
    return <div className="p-6 text-center text-muted-foreground text-sm">Cargando...</div>;
  }
  if (!data) return null;

  const nonCanonicalRows = data.rows.filter((r) => !r.is_canonical);

  return (
    <div className="space-y-4 mt-2" data-testid="status-audit-panel">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Unidades totales (no borradas)</p>
            <p className="text-2xl font-bold">{data.total_units}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Status canónicos</p>
            <p className="text-2xl font-bold text-emerald-500">{data.total_units - data.non_canonical_count}</p>
          </CardContent>
        </Card>
        <Card className={data.non_canonical_count > 0 ? 'border-amber-500/50' : ''}>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Status no canónicos</p>
            <p className={`text-2xl font-bold ${data.non_canonical_count > 0 ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {data.non_canonical_count}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Header + reload */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Database className="h-5 w-5" />
            Auditoría de Status
          </h3>
          <p className="text-xs text-muted-foreground">
            Detecta unidades con valores de status legacy o inconsistentes y permite normalizarlas en bloque.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="status-audit-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Recargar
        </Button>
      </div>

      {/* All status rows table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Distribución actual</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm">
            <div className="grid grid-cols-[1fr_80px_120px_1fr_120px] gap-2 px-2 py-1 text-xs uppercase tracking-wider text-muted-foreground border-b">
              <div>Status</div>
              <div className="text-right">Unidades</div>
              <div>Canónico</div>
              <div>Sugerido</div>
              <div></div>
            </div>
            {data.rows.map((r) => (
              <div
                key={r.raw_status ?? '__null__'}
                className={`grid grid-cols-[1fr_80px_120px_1fr_120px] gap-2 px-2 py-2 items-center border-b last:border-b-0 ${!r.is_canonical ? 'bg-amber-500/5' : ''}`}
                data-testid={`status-row-${r.raw_status ?? 'empty'}`}
              >
                <code className="text-xs font-mono">{r.status}</code>
                <div className="text-right font-mono font-semibold">{r.count}</div>
                <div>
                  {r.is_canonical ? (
                    <Badge className="bg-emerald-600 text-xs">✓ Canónico</Badge>
                  ) : (
                    <Badge className="bg-amber-600 text-xs">⚠ Legacy</Badge>
                  )}
                </div>
                <div>
                  {!r.is_canonical && (
                    <Select
                      value={overrideTarget[r.raw_status ?? ''] ?? r.suggested ?? ''}
                      onValueChange={(v) => setOverrideTarget((prev) => ({ ...prev, [r.raw_status ?? '']: v }))}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`status-target-${r.raw_status ?? 'empty'}`}>
                        <SelectValue placeholder="Elegir destino..." />
                      </SelectTrigger>
                      <SelectContent>
                        {data.canonical_values.map((cv) => (
                          <SelectItem key={cv} value={cv} className="text-xs font-mono">{cv}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="flex justify-end">
                  {!r.is_canonical && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={normalizing === r.raw_status}
                      onClick={() => normalize(r.raw_status, overrideTarget[r.raw_status ?? ''] ?? r.suggested)}
                      data-testid={`status-normalize-${r.raw_status ?? 'empty'}`}
                    >
                      {normalizing === r.raw_status ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      )}
                      Normalizar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {nonCanonicalRows.length === 0 && (
        <div className="text-center text-sm text-emerald-500 p-4 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
          <CheckCircle className="h-5 w-5 mx-auto mb-1" />
          Todos los status están en valores canónicos. La base de datos está limpia.
        </div>
      )}

      {/* v5.4.9 — Orphaned Lost Units cleanup */}
      <OrphanedLostPanel />
    </div>
  );
};


// ============================================================
// v5.4.9 — Orphaned Lost Units Panel
// Detects and recovers units stuck in perdido_* status whose source movement
// was soft-deleted (so the tracking page no longer attributes them to ghost
// events like "Prueba")
// ============================================================
const OrphanedLostPanel = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [recovering, setRecovering] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/equipment-units/orphaned-lost');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el listado');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const recover = async () => {
    if (!data || data.orphan_count === 0) return;
    const cd = data.by_cause || { movement_deleted: 0, no_source: 0 };
    const msg = `¿Procesar ${data.orphan_count} unidad(es) huérfana(s)?\n\n` +
      `• ${cd.movement_deleted} con causa "movimiento eliminado" → se SOFT-DELETE (no aparecerán más en inventario ni estadísticas)\n` +
      `• ${cd.no_source} con causa "sin fuente" → se recuperan a Disponible`;
    if (!window.confirm(msg)) return;
    setRecovering(true);
    try {
      const res = await api.post('/equipment-units/recover-orphaned-lost');
      const parts = [];
      if (res.data.soft_deleted) parts.push(`${res.data.soft_deleted} soft-deleted`);
      if (res.data.recovered) parts.push(`${res.data.recovered} recuperada(s) a Disponible`);
      toast.success(parts.join(' · ') || 'Sin cambios');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo procesar');
    } finally {
      setRecovering(false);
    }
  };

  const runCheckNow = async () => {
    try {
      await api.post('/equipment-units/orphan-check-now');
      toast.success('Chequeo ejecutado. Si hay huérfanas, los admins recibirán email.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo ejecutar el chequeo');
    }
  };

  if (!data) return null;

  return (
    <Card className="border-rose-500/40" data-testid="orphan-lost-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-5 w-5 text-rose-500" />
          Unidades Perdidas Huérfanas
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Unidades en estado <code>perdido_recuperar / perdido_definitivo</code> cuyo movimiento fuente
          ya fue eliminado (no hay ningún movimiento activo que las marque como perdidas).
          La página de Rastreo ya no las atribuye al evento eliminado, pero siguen contando como "perdidas" en los KPIs.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <p className="text-xs text-muted-foreground">Perdidas (total)</p>
            <p className="text-2xl font-bold">{data.total_lost}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Huérfanas detectadas</p>
            <p className={`text-2xl font-bold ${data.orphan_count > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
              {data.orphan_count}
            </p>
          </div>
        </div>

        {data.orphan_count > 0 ? (
          <>
            <div className="max-h-64 overflow-y-auto border rounded-lg mb-3">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Equipo</th>
                    <th className="text-left p-2">S/N</th>
                    <th className="text-left p-2">Status actual</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orphans.map((o) => (
                    <tr key={o.unit_id} className="border-t" data-testid={`orphan-row-${o.unit_id}`}>
                      <td className="p-2">{o.equipment_name}</td>
                      <td className="p-2 font-mono">{o.serial_number}</td>
                      <td className="p-2"><Badge variant="outline" className="text-xs">{o.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={load} variant="outline" size="sm" disabled={loading}>
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Recargar
              </Button>
              <Button onClick={runCheckNow} variant="outline" size="sm" data-testid="orphan-check-now-btn">
                <Mail className="h-3 w-3 mr-1" />
                Probar chequeo + email
              </Button>
              <Button
                onClick={recover}
                size="sm"
                disabled={recovering}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="recover-orphan-lost-btn"
              >
                {recovering ? (
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <CheckCircle className="h-3 w-3 mr-1" />
                )}
                Procesar todas (auto)
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center text-sm text-emerald-500 p-4 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
            <CheckCircle className="h-5 w-5 mx-auto mb-1" />
            Sin unidades huérfanas. Toda unidad perdida tiene un movimiento activo que la respalda.
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ============================================================
// v5.4.11 — Inventory Health Panel
// One-stop dashboard for superadmins: orphans, non-canonical status,
// untracked units, AUTO-* legacy serials, deletion ratios, health score
// ============================================================
const InventoryHealthPanel = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/inventory-health');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la salud del inventario');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-6 text-center text-muted-foreground text-sm">Cargando salud del inventario...</div>;
  }
  if (!data) return null;

  const score = data.health_score;
  const scoreColor =
    score >= 95 ? 'text-emerald-500' :
    score >= 80 ? 'text-amber-500' :
    'text-rose-500';

  const Tile = ({ label, value, sub, tone = 'neutral', testid }) => {
    const toneClass =
      tone === 'good' ? 'text-emerald-500' :
      tone === 'warn' ? 'text-amber-500' :
      tone === 'bad'  ? 'text-rose-500'    :
      'text-foreground';
    return (
      <Card data-testid={testid}>
        <CardContent className="pt-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className={`text-3xl font-bold ${toneClass}`}>{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </CardContent>
      </Card>
    );
  };

  const u = data.units;
  const o = data.orphans;
  const m = data.movements;

  return (
    <div className="space-y-4 mt-2" data-testid="inventory-health-panel">
      {/* Header + Score */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Salud del Inventario
          </h3>
          <p className="text-xs text-muted-foreground">
            Snapshot de integridad de datos para superadmins · generado {new Date(data.generated_at).toLocaleString('es-MX')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Score</p>
            <p className={`text-4xl font-bold ${scoreColor}`}>{score}</p>
          </div>
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="health-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recargar
          </Button>
        </div>
      </div>

      {/* Units row */}
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Unidades</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Tile label="Total activas" value={u.total} testid="health-units-total" />
          <Tile label="Soft-deleted" value={u.soft_deleted} sub="No aparecen en inventario" tone="neutral" testid="health-units-deleted" />
          <Tile label="Status no canónico" value={u.non_canonical_status} tone={u.non_canonical_status > 0 ? 'warn' : 'good'} testid="health-noncanon" />
          <Tile label="Sin condition log" value={u.without_condition_log} sub="Nunca trackeadas" tone={u.without_condition_log > 0 ? 'warn' : 'good'} testid="health-nolog" />
          <Tile label="Serial AUTO-*" value={u.auto_serial_legacy} sub="Legacy auto-asignados" tone={u.auto_serial_legacy > 0 ? 'warn' : 'good'} testid="health-auto" />
          <Tile label="Serial NA" value={u.na_serial} sub="Manualmente sin S/N" testid="health-na" />
        </div>
      </div>

      {/* Orphans row */}
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Unidades Perdidas Huérfanas</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total perdidas" value={o.total_lost} testid="health-lost-total" />
          <Tile label="Huérfanas" value={o.orphan_count} tone={o.orphan_count > 0 ? 'bad' : 'good'} testid="health-orphans" />
          <Tile label="→ Soft-delete (mov. eliminado)" value={o.by_cause?.movement_deleted ?? 0} tone={o.by_cause?.movement_deleted > 0 ? 'warn' : 'good'} testid="health-orphans-deleted" />
          <Tile label="→ Recuperar (sin fuente)" value={o.by_cause?.no_source ?? 0} testid="health-orphans-nosource" />
        </div>
      </div>

      {/* Movements row */}
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Movimientos</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Activos" value={m.active} testid="health-mov-active" />
          <Tile label="Eliminados" value={m.deleted} tone="neutral" testid="health-mov-deleted" />
          <Tile label="Eliminados últimos 30d" value={m.deleted_last_30d} testid="health-mov-recent-deleted" />
          <Tile label="Creados últimos 30d" value={m.created_last_30d} testid="health-mov-recent-created" />
          <Tile
            label="Ratio eliminación 30d"
            value={`${m.deleted_ratio_30d_percent}%`}
            tone={m.deleted_ratio_30d_percent > 30 ? 'warn' : 'good'}
            sub={m.deleted_ratio_30d_percent > 30 ? 'Alto — investigar' : 'Saludable'}
            testid="health-mov-ratio"
          />
        </div>
      </div>

      {/* Equipment row */}
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Catálogo de Equipos</p>
        <div className="grid grid-cols-2 gap-3">
          <Tile label="Activos" value={data.equipment.total} testid="health-eq-total" />
          <Tile label="Soft-deleted" value={data.equipment.soft_deleted} testid="health-eq-deleted" />
        </div>
      </div>

      {/* Action shortcut */}
      {o.orphan_count > 0 && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="pt-4">
            <p className="text-sm">
              ⚠ Hay <b className="text-rose-500">{o.orphan_count}</b> unidad(es) huérfana(s).
              Ve a la pestaña <b>Status Audit</b> → <b>Unidades Perdidas Huérfanas</b> para procesarlas.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
